import React, { useState, useEffect } from 'react';
import { generateQRCodeDataUrl } from '../../utils/qrCode';

const STANDARD_PRESETS = [
  { id: 'square-sm', label: 'Small Square', width: 1, height: 1, unit: 'in' },
  { id: 'square-md', label: 'Medium Square', width: 2, height: 2, unit: 'in' },
  { id: 'square-lg', label: 'Large Square', width: 3, height: 3, unit: 'in' },
  { id: 'custom', label: 'Custom', width: 2, height: 2, unit: 'in', isCustom: true },
];

const DPI = 96; // Standard screen DPI

const QRCodeSizeSelector = ({ 
  item, 
  onGenerate, 
  onClose, 
  actionType = 'download' // 'download' or 'print'
}) => {
  const [selectedPreset, setSelectedPreset] = useState('square-md');
  const [customSize, setCustomSize] = useState({ width: 2, height: 2, unit: 'in' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    generatePreview();
  }, [selectedPreset, customSize]);

  const generatePreview = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const preset = STANDARD_PRESETS.find(p => p.id === selectedPreset) || 
                   { ...customSize, isCustom: true };
      
      const width = preset.isCustom ? customSize.width : preset.width;
      const height = preset.isCustom ? customSize.height : preset.height;
      
      // Convert inches to pixels for display
      const widthPx = Math.round(width * DPI);
      const heightPx = Math.round(height * DPI);
      
      const qrDataUrl = await generateQRCodeDataUrl(
        item.id, 
        item.name,
        widthPx,
        heightPx,
        'standard'
      );
      
      setPreviewUrl(qrDataUrl);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate QR code preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId);
  };

  const handleCustomSizeChange = (e) => {
    const { name, value } = e.target;
    setCustomSize(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleGenerate = () => {
    const preset = STANDARD_PRESETS.find(p => p.id === selectedPreset) || 
                 { ...customSize, isCustom: true };
    
    const width = preset.isCustom ? customSize.width : preset.width;
    const height = preset.isCustom ? customSize.height : preset.height;
    
    onGenerate({
      width: Math.round(width * DPI),
      height: Math.round(height * DPI),
      displayWidth: width,
      displayHeight: height,
      unit: preset.unit || 'in',
      qrType: 'standard'
    });
  };

  const currentPreset = STANDARD_PRESETS.find(p => p.id === selectedPreset) || 
                     { ...customSize, isCustom: true };
  const isCustom = currentPreset.isCustom || selectedPreset === 'custom';
  const displayWidth = isCustom ? customSize.width : currentPreset.width;
  const displayHeight = isCustom ? customSize.height : currentPreset.height;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              {actionType === 'download' ? 'Download' : 'Print'} QR Code
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Size Presets */}
            <div>
              <h3 className="font-medium mb-3">QR Code Size Presets</h3>
              <div className="grid grid-cols-2 gap-2">
                {STANDARD_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetSelect(preset.id)}
                    className={`p-3 border rounded-md text-left transition-colors ${
                      selectedPreset === preset.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">
                      {preset.label}
                      {preset.isCustom && ' (Custom)'}
                    </div>
                    {!preset.isCustom && (
                      <div className="text-sm text-gray-500">
                        {preset.width} × {preset.height} {preset.unit}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {isCustom && (
                <div className="mt-4 p-4 border border-gray-200 rounded-md">
                  <h4 className="font-medium mb-3">Custom Size</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Width ({customSize.unit})
                      </label>
                      <input
                        type="number"
                        name="width"
                        min="0.5"
                        step="0.1"
                        value={customSize.width}
                        onChange={handleCustomSizeChange}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Height ({customSize.unit})
                      </label>
                      <input
                        type="number"
                        name="height"
                        min="0.5"
                        step="0.1"
                        value={customSize.height}
                        onChange={handleCustomSizeChange}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Preview */}
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">
                QR Code Preview
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({displayWidth} × {displayHeight} {currentPreset.unit})
                </span>
              </h3>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-md p-4 flex items-center justify-center bg-gray-50">
              {isLoading ? (
                <div className="py-12 text-gray-500">Generating preview...</div>
              ) : error ? (
                <div className="text-red-500 py-12">{error}</div>
              ) : (
                <div 
                  className="bg-white p-2 shadow-sm"
                  style={{
                    width: `${displayWidth * 50}px`,
                    height: `${displayHeight * 50}px`,
                    maxWidth: '100%',
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img 
                    src={previewUrl} 
                    alt="QR Code Preview" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md text-white ${
                  isLoading 
                    ? 'bg-blue-300 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isLoading ? 'Generating...' : 
                 actionType === 'download' ? 
                 'Download QR' : 
                 'Print QR'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeSizeSelector;
